// src/types/features.ts
// Сообщения нод робота audio / bt_manager / vision (robot/rpi5/.../fleetcore_robot)
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { NotificationItem } from './index';

export interface FeatureProps {
  zenohSession: zenoh.Session | null;
  robotId: string;
  onNotify: (message: string, type?: NotificationItem['type']) => void;
}

// robot/<id>/bt/state
export interface BtDevice {
  mac: string;
  name: string;
  icon: string;
  paired: boolean;
  trusted: boolean;
  connected: boolean;
  audio: boolean;
  rssi: number | null;
  battery: number | null;
  priority: number | null;
  auto: boolean;
}

export interface BtState {
  adapter: { ok: boolean; powered: boolean; error: string | null };
  auto: boolean;
  switch_higher: boolean;
  scanning: boolean;
  busy: string | null;
  error: string | null;
  devices: BtDevice[];
  timestamp: number;
}

// robot/<id>/audio/state
export interface NannySettings {
  enabled: boolean;
  loud_db: number;
  rel_db: number;
  unusual: boolean;
  unusual_z: number;
  keywords: string[];
  record: 'events' | 'continuous' | 'off';
  cooldown_s: number;
  retention_mb: number;
  volume: number;
  mic_gain_db?: number;  // программное усиление микрофона на роботе, дБ
  mic_ns?: number;       // шумоподавление 0 (выкл) … 4
  mic_agc?: boolean;     // автоусиление речи
  butler?: boolean;               // дворецкий «Грач»: микрофон слушает всегда
  butler_names?: string[];        // на какое имя откликается
  butler_confirm?: 'beep' | 'voice' | 'off';  // ответ на понятую команду: сигнал, голосом, молча
  butler_grammar?: boolean;       // распознавать только слова команд (точнее)
  asr_ns?: number;                // шумоподавление для распознавания 0…4 (для прослушивания — mic_ns)
  asr_agc?: boolean;              // автоусиление для распознавания
  butler_keep_audio?: boolean;    // сохранять звук последних фраз на роботе (для настройки)
}

export interface Recording {
  name: string;
  size: number;
  duration: number;
  type: string;
  timestamp: number;
}

export interface AudioState {
  mic: { ok: boolean | null; on: boolean; error: string | null };
  speaker: boolean;
  tts: boolean;
  speaking: boolean;
  recognition: boolean;
  denoise?: boolean;     // на роботе есть webrtc-noise-gain
  level_db: number | null;
  floor_db: number | null;
  listening: boolean;
  asleep?: boolean;  // режим сна: прослушивание выключено (няня работает)
  recording: boolean;
  // Дворецкий: слушает ли сейчас (включён и есть Vosk), ждёт ли команду после имени, что услышал
  // recent — последние фразы распознавателя: command / maybe (имя послышалось, «врач …») / armed / ignored
  // model — кто распознаёт команды после имени: GigaAM (если стоит на роботе) или Vosk
  butler?: { on: boolean; armed: boolean; heard: { text: string; at: number } | null; model?: string;
             recent?: { text: string; at: number; kind: 'command' | 'maybe' | 'armed' | 'ignored'; free?: string }[] };
  nanny: NannySettings;
  recordings: Recording[];
  timestamp: number;
}

// robot/<id>/audio/event
export interface AudioEvent {
  type: 'loud' | 'noise' | 'unusual' | 'keyword' | 'voice_command' | 'voice_text' | 'error' | string;
  text: string;
  level_db?: number;
  keyword?: string;
  heard?: string;
  clip?: string | null;
  source?: string;
  timestamp: number;
}

// robot/<id>/vision/state
export interface VisionConfig {
  enabled: boolean;
  speak: boolean;
  alerts_only: boolean;
  min_score: number;
  people: boolean;
  faces: boolean;
  pose: boolean;
  objects: boolean;
  signs: boolean;
  classes: string[];
  min_interval_s: number;
  map_objects: boolean;
  map_classes: string[];
}

export interface VisionState {
  config: VisionConfig;
  backends: Record<string, string | boolean | null>;
  faces: Record<string, number>;
  camera: boolean;
  asleep?: boolean;  // режим сна: распознавание выключено
  fps: number;
  error: string | null;
  classes: Record<string, string>;
  timestamp: number;
}

// robot/<id>/vision/events
export interface VisionEvent {
  kind: string;
  text: string;
  alert: boolean;
  timestamp: number;
}

// robot/<id>/vision/detections
export interface Detections {
  timestamp: number;
  objects: { label: string; label_ru: string; score: number; box: number[]; name: string | null; posture: string | null }[];
  faces: { box: number[]; name: string | null }[];
  signs: { kind: string; text: string; box: number[] }[];
}

export const NANNY_EVENT_TYPES = ['loud', 'noise', 'unusual', 'keyword'];

export function timeOf(ms: number): string {
  return new Date(ms).toLocaleTimeString();
}

// Данные устарели (робот/нода не отвечает)
export function isStale(ts: number | undefined, ms = 6000): boolean {
  return !ts || Date.now() - ts > ms;
}

// Настройка «для этого браузера» (не синхронизируется с роботом)
export function loadPref<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

export function savePref(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* без запоминания */
  }
}
